import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TableCell,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FilterList as FilterIcon, Menu as MenuIcon } from '@mui/icons-material';

/** The minimum a project needs to be pickable. */
export interface ProjectOption {
  projectId: string;
  projectName: string | null;
}

export const paHeadCellSx = {
  backgroundColor: 'var(--color-surface-thead)',
  color: 'var(--color-text-strong)',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  zIndex: 3,
  boxShadow: 'inset 0 -1px 0 rgb(var(--color-border-rgb) / 0.12)',
};

export const paTableContainerSx = {
  maxHeight: 'min(52vh, 560px)',
  overflow: 'auto',
} as const;

export function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="body2"
    sx={{ color: 'rgb(var(--color-text-rgb) / 0.6)', py: 4, textAlign: 'center' }}
  >
    {children}
  </Typography>
);

/**
 * A free-text cell that saves when you click away. Used for both comments in
 * this module — the lead's brief and the caller's note — because either is
 * usually a few words typed in passing, and a modal per row would cost more
 * than it is worth.
 */
export const InlineCommentCell: React.FC<{
  value: string | null;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  onSave: (next: string) => Promise<void>;
}> = ({ value, placeholder, ariaLabel, disabled = false, onSave }) => {
  const stored = value || '';
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Follow the stored note when it changes underneath (a refresh, or a save
  // that trimmed what was typed).
  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const handleBlur = async () => {
    const next = draft.trim();
    if (next === stored.trim()) return;
    setSaving(true);
    try {
      await onSave(next);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TextField
      size="small"
      fullWidth
      multiline
      maxRows={4}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled || saving}
      placeholder={placeholder}
      helperText={saving ? 'Saving…' : justSaved ? 'Saved' : ' '}
      FormHelperTextProps={{
        sx: { m: 0, minHeight: 16, color: 'rgb(var(--color-text-rgb) / 0.5)' },
      }}
      inputProps={{ 'aria-label': ariaLabel }}
    />
  );
};

/* ---------------------------------------------------------------------------
 * Column sort + filter, in the Project Research style: a menu button beside the
 * column label opening a popover with a Default/Asc/Desc toggle and that
 * column's filter control. Shared so both Project Action tables behave alike.
 * ------------------------------------------------------------------------- */

export type SortDirection = 'default' | 'asc' | 'desc';

/**
 * Header cell with an optional menu button. The button takes the accent colour
 * while its column is sorted or filtered — a popover hides its own state, and
 * without the cue it is easy to stare at a short table wondering where the rest
 * of the rows went.
 */
export const ColumnHeadCell: React.FC<{
  label: string;
  active?: boolean;
  minWidth?: number;
  align?: 'left' | 'center';
  padding?: 'checkbox' | 'normal';
  onOpenMenu?: (element: HTMLElement) => void;
}> = ({ label, active = false, minWidth, align = 'left', padding, onOpenMenu }) => (
  <TableCell
    sx={{ ...paHeadCellSx, ...(minWidth ? { minWidth } : {}) }}
    padding={padding === 'checkbox' ? 'checkbox' : undefined}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'center' ? 'center' : 'flex-start',
        gap: 0.5,
      }}
    >
      {label}
      {onOpenMenu && (
        <IconButton
          size="small"
          onClick={(e) => onOpenMenu(e.currentTarget)}
          sx={{
            p: 0.5,
            color: active ? 'var(--color-accent)' : 'var(--color-text-strong)',
            '&:hover': { bgcolor: 'rgb(var(--color-text-rgb) / 0.1)', color: 'var(--color-accent)' },
          }}
          aria-label={`Sort and filter ${label}`}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  </TableCell>
);

/** The popover a `ColumnHeadCell` menu button opens. `children` is the filter control. */
export const ColumnMenuPopover: React.FC<{
  open: boolean;
  anchorEl: HTMLElement | null;
  label: string;
  direction: SortDirection;
  onDirectionChange: (direction: SortDirection) => void;
  onClose: () => void;
  onClear: () => void;
  clearDisabled?: boolean;
  children: React.ReactNode;
}> = ({
  open,
  anchorEl,
  label,
  direction,
  onDirectionChange,
  onClose,
  onClear,
  clearDisabled = false,
  children,
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
  >
    <Box sx={{ p: 2, minWidth: 240 }}>
      <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
        Sort
      </Typography>
      <ToggleButtonGroup
        value={direction}
        exclusive
        onChange={(_e, value) => {
          if (value === null) return;
          onDirectionChange(value as SortDirection);
        }}
        fullWidth
        sx={{
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid rgb(var(--color-shadow-rgb) / 0.12)',
            '&:not(:first-of-type)': {
              borderLeft: '1px solid rgb(var(--color-shadow-rgb) / 0.12)',
              marginLeft: 0,
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--color-info)',
              color: 'var(--color-text-strong)',
              '&:hover': { backgroundColor: 'var(--color-info-strong)' },
            },
          },
        }}
      >
        <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>
          Default
        </ToggleButton>
        <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>
          Asc
        </ToggleButton>
        <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>
          Desc
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
        <FilterIcon sx={{ color: 'var(--color-info)', mr: 1, fontSize: 18 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {`Filter ${label}`}
        </Typography>
      </Box>
      {children}
      <Button size="small" sx={{ mt: 1 }} onClick={onClear} disabled={clearDisabled}>
        Clear
      </Button>
    </Box>
  </Popover>
);

/** Free-text "contains" filter matcher used by both tables. */
export function matchesText(haystack: string | null | undefined, needle: string): boolean {
  const term = needle.trim().toLowerCase();
  if (!term) return true;
  return (haystack || '').toLowerCase().includes(term);
}

/**
 * The leading number in a remark like "7 listings", so a "Why" column sorts by
 * how many rather than alphabetically (where "10" would land before "2").
 */
export function leadingNumber(value: string | null): number | null {
  const match = /(-?\d+(?:\.\d+)?)/.exec(value || '');
  return match ? Number(match[1]) : null;
}

/** Shared WhatsApp button styling — brand green, never mistaken for disabled. */
export const whatsappButtonSx = {
  bgcolor: '#25D366',
  color: '#08331C',
  fontWeight: 700,
  whiteSpace: 'nowrap' as const,
  '&:hover': { bgcolor: '#1FB855' },
};
